"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";

import { Button } from "@/components/buttons/Button";
import { DowngradeSubscriptionModal } from "@/components/pricing/pricingButton/downgradeSubscriptionModal/DowngradeSubscriptionModal";
import { PRICE_PLAN_BUTTON_LABELS } from "@/constants/pricePlanButtonLabels";
import { createStripeCheckoutAction } from "@/data/payment/actions/createStripeCheckoutAction";
import { createStripePortalAction } from "@/data/payment/actions/createStripePortalAction";
import { renewStripeSubscriptionAction } from "@/data/payment/actions/renewStripeSubscriptionAction";
import { useSetAuthModalOpen } from "@/lib/clerk/useSetAuthModalOpen";
import { User, UserNoDates } from "@/types/types";
import { cn } from "@/utils/cn";
import { getSubscriptionButtonLabel } from "@/utils/subscriptionLevels/getSubscriptionButtonLabel";

export function PricingButton({
  user,
  stripeSubscriptionId,
  numberOfModels,
  closeModal,
}: {
  user: UserNoDates | null;
  stripeSubscriptionId: User["stripeSubscriptionId"];
  numberOfModels: number;
  closeModal: (() => void) | null;
}) {
  const [downgradeModalOpen, setDowngradeModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const setAuthModalOpen = useSetAuthModalOpen();

  const router = useRouter();

  const loggedIn = user !== null;

  const buttonText = getSubscriptionButtonLabel({
    loggedIn,
    stripeSubscriptionId,
    user,
  });

  const getPlan = async () => {
    if (stripeSubscriptionId === null) {
      return;
    }

    const stripeCheckoutUrl = await createStripeCheckoutAction({
      priceId: stripeSubscriptionId,
      toltId: window.tolt_referral ?? null,
    });

    if (stripeCheckoutUrl === null) {
      return;
    }

    router.push(stripeCheckoutUrl);
  };

  const upgradeSubscription = async () => {
    if (stripeSubscriptionId === null) {
      return;
    }

    const stripePortalUrl = await createStripePortalAction({
      typeOfPortal: "subscriptionUpdateConfirm",
      stripeIdPriceForSubscriptionUpdateConfirm: stripeSubscriptionId,
    });

    if (stripePortalUrl === null) {
      return;
    }

    router.push(stripePortalUrl);
  };

  const handleLoadingState = async (callback: () => Promise<void | null>) => {
    setIsLoading(true);
    try {
      await callback();
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlanChange = async () => {
    if (isLoading) {
      return;
    }

    if (buttonText === PRICE_PLAN_BUTTON_LABELS.GET_STARTED) {
      router.push("/create");
      closeModal?.();
      return;
    }

    if (!loggedIn) {
      setAuthModalOpen();
      return;
    }

    switch (buttonText) {
      case PRICE_PLAN_BUTTON_LABELS.GET_PLAN: {
        await handleLoadingState(getPlan);
        return;
      }
      case PRICE_PLAN_BUTTON_LABELS.MANAGE: {
        router.push("/account");
        closeModal?.();
        return;
      }
      case PRICE_PLAN_BUTTON_LABELS.RENEW: {
        await handleLoadingState(renewStripeSubscriptionAction);
        return;
      }
      case PRICE_PLAN_BUTTON_LABELS.UPGRADE:
      case PRICE_PLAN_BUTTON_LABELS.GO_MONTHLY:
      case PRICE_PLAN_BUTTON_LABELS.GO_YEARLY: {
        await handleLoadingState(upgradeSubscription);
        return;
      }
      case PRICE_PLAN_BUTTON_LABELS.DOWNGRADE: {
        setDowngradeModalOpen(true);
        return;
      }
      default:
        return;
    }
  };

  return (
    <>
      <DowngradeSubscriptionModal
        open={downgradeModalOpen}
        closeModal={() => setDowngradeModalOpen(false)}
        user={user}
        numberOfModels={numberOfModels}
        downgradeStripeSubscriptionId={stripeSubscriptionId}
      />
      <Button
        onClick={handlePlanChange}
        ariaDescribedby={stripeSubscriptionId === null ? undefined : "free"}
        loading={isLoading}
        disabled={isLoading}
        variant="primary"
        className={cn(
          buttonText === PRICE_PLAN_BUTTON_LABELS.GET_STARTED &&
            "from-gray-200 via-gray-200 to-gray-200 text-gray-900",
          (buttonText === PRICE_PLAN_BUTTON_LABELS.MANAGE ||
            buttonText === PRICE_PLAN_BUTTON_LABELS.RENEW) &&
            "from-primaryLight via-primaryLight to-primaryLight text-gray-900",
          "mt-6 w-full text-center lg:mt-10"
        )}
      >
        {buttonText}
      </Button>
    </>
  );
}
